import React, { CSSProperties, Fragment, useEffect, useState } from "react";
import {
  Table,
  TableContainer,
  TableHead,
  TableCell,
  TableBody,
  TableRow,
  useTheme,
  useMediaQuery,
  Theme,
  Typography,
  Stack,
  Skeleton,
  Divider,
} from "@mui/material";
import PaginationCustom from "@components/Pagination";
import { Drivers } from "../models";
import { LinkModal } from "./LinkModal";
import { useMyDriversContext } from "@context/MyDriversContext";
import Button from "@components/Button";
import { format } from "date-fns";
import { headers } from "../constants";

const ROWS_PER_PAGE = 10;

// --- Helpers ---
const translateStatus = (status: string) => {
  switch (status) {
    case "INACTIVE":
      return "Inativo";
    case "ACTIVE":
      return "Ativo";
    default:
      return "Desconhecido";
  }
};

// --- Mobile Card Component (extracted) ---
interface DriverCardProps {
  data: Drivers;
  loading: boolean;
  onEdit: (driver: Drivers) => void;
}

const DriverCard = React.memo<DriverCardProps>(({ data, loading, onEdit }) => {
  const theme = useTheme();
  if (loading) {
    return (
      <Stack
        sx={{
          width: "100%",
          p: 2,
          background: theme.palette.background.paper,
          borderRadius: "12px",
          border: `1px solid ${theme.palette.grey[200]}`,
        }}
      >
        <Skeleton animation="wave" height="2.5rem" />
        <Skeleton animation="wave" height="2rem" width="80%" />
        <Skeleton animation="wave" height="2rem" width="50%" />
        <Skeleton
          animation="wave"
          height="44px"
          sx={{ mt: 1, borderRadius: "8px" }}
        />
      </Stack>
    );
  }

  return (
    <Stack
      sx={{
        width: "100%",
        p: 2,
        background: theme.palette.background.paper,
        borderRadius: "12px",
        border: `1px solid ${theme.palette.grey[200]}`,
      }}
    >
      <Stack spacing={0.8}>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "1.5rem",
            color: theme.palette.grey[800],
            lineHeight: 1.3,
          }}
        >
          {data.name}
        </Typography>

        <Typography
          sx={{
            fontSize: "1.25rem",
            color: theme.palette.grey[600],
            wordBreak: "break-word",
            lineHeight: 1.4,
          }}
        >
          {data.email}
        </Typography>

        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Typography
            sx={{ fontSize: "1.2rem", color: theme.palette.grey[500] }}
          >
            Status:
          </Typography>
          <Typography
            sx={{
              fontSize: "1.2rem",
              fontWeight: 600,
              color:
                data.status === "ACTIVE"
                  ? theme.palette.success.main
                  : theme.palette.error.main,
            }}
          >
            {translateStatus(data.status)}
          </Typography>
        </Stack>

        <Divider sx={{ my: 1 }} />

        <Typography
          sx={{
            fontSize: "1.15rem",
            fontWeight: 600,
            color: theme.palette.grey[500],
            mb: 0.5,
          }}
        >
          Clientes vinculados:
        </Typography>
        <Stack direction="row" flexWrap="wrap" gap={0.8}>
          {data.companies.slice(0, 3).map((item) => (
            <Typography
              key={`${item.companyName}-${item.CNPJ}`}
              sx={{
                border: `1px solid ${theme.palette.grey[300]}`,
                borderRadius: "6px",
                px: 1,
                py: 0.4,
                fontSize: "1.15rem",
              }}
            >
              {item.companyName}
            </Typography>
          ))}
          {data.companies.length > 3 && (
            <Typography
              sx={{
                backgroundColor: "#FFF5F7",
                color: theme.palette.error.main,
                fontWeight: 600,
                fontSize: "1.15rem",
                borderRadius: "12px",
                px: 1,
                py: 0.4,
              }}
            >
              +{data.companies.length - 3}
            </Typography>
          )}
        </Stack>

        <Button
          variant="outlined"
          fullWidth
          onClick={() => onEdit(data)}
          sx={{
            fontWeight: 600,
            minHeight: "48px",
            mt: 1.5,
            borderRadius: "8px",
            fontSize: "1.3rem",
          }}
        >
          Editar Vínculos
        </Button>
      </Stack>
    </Stack>
  );
});
DriverCard.displayName = "DriverCard";

// --- Desktop Table Row Component (extracted) ---
interface DriverRowProps {
  data: Drivers;
  loading: boolean;
  onEdit: (driver: Drivers) => void;
  styles: Record<string, CSSProperties>;
}

const DriverRow = React.memo<DriverRowProps>(
  ({ data, loading, onEdit, styles }) => {
    const items = [data.name, data.email, translateStatus(data.status)];

    return (
      <TableRow>
        {items.map((item, index) => (
          <TableCell
            key={`${item}+${index + 1}`}
            sx={
              !index
                ? { ...styles.table_first_column, ...styles.table_cells }
                : styles.table_cells
            }
          >
            {loading ? <Skeleton animation="wave" height="5rem" /> : item}
          </TableCell>
        ))}
        <TableCell align="center" sx={{ border: "none" }}>
          {loading ? (
            <Skeleton animation="wave" height="5rem" />
          ) : (
            <Stack sx={styles.linked_companies}>
              {data.companies.slice(0, 3).map((item) => (
                <Typography
                  key={`${item.companyName}-${item.CNPJ}`}
                  sx={styles.tag_company}
                >
                  {item.companyName}
                </Typography>
              ))}

              {data.companies.length > 3 && (
                <Typography sx={styles.more_companies}>
                  +{data.companies.length - 3}
                </Typography>
              )}
            </Stack>
          )}
        </TableCell>

        <TableCell align="right" sx={{ border: "none" }}>
          {loading ? (
            <Skeleton animation="wave" height="5rem" />
          ) : (
            <Button onClick={() => onEdit(data)} sx={{ fontWeight: 600 }}>
              Editar
            </Button>
          )}
        </TableCell>
      </TableRow>
    );
  }
);
DriverRow.displayName = "DriverRow";

// --- Main Component ---
const MyDriversTable: React.FC = () => {
  const [showLink, setShowLink] = useState<boolean>(false);
  const [linkData, setLinkData] = useState<{
    id: string;
    name: string;
    companies: Drivers["companies"];
  }>(
    {} as {
      id: string;
      name: string;
      companies: Drivers["companies"];
    }
  );

  const {
    data,
    page,
    loading,
    setListCompany,
    setPage,
    fetchAllDrivers,
  } = useMyDriversContext();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const styles = useStyles(theme);

  const handleOnChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleOnChange = (
    event: React.ChangeEvent<unknown>,
    newPage: number
  ) => {
    setPage(newPage - 1);
  };

  const handleLinkData = (driver: Drivers) => {
    const dataWithFormattedDate = driver.companies.map((items) => ({
      ...items,
      lastSchedule:
        items.lastSchedule !== null
          ? format(
              new Date(items.lastSchedule as string),
              "dd/MM/yyyy 'às' HH:mm"
            )
          : "-",
    }));

    setLinkData({
      id: driver.id,
      name: driver.name,
      companies: dataWithFormattedDate,
    });

    setShowLink(true);

    setListCompany((prevState) => ({
      ...prevState,
      companyDocuments: driver.companies.map((item) => item.CNPJ),
    }));
  };

  useEffect(() => {
    fetchAllDrivers();
  }, [fetchAllDrivers]);

  return (
    <Fragment>
      {showLink && (
        <LinkModal
          open={showLink}
          onClose={() => setShowLink(false)}
          data={linkData}
        />
      )}

      {isMobile ? (
        <Stack spacing={2} sx={{ mt: 2, mb: 2, width: "100%" }}>
          {data.rows.map((item) => (
            <DriverCard
              key={String(item.id)}
              data={item}
              loading={loading}
              onEdit={handleLinkData}
            />
          ))}
        </Stack>
      ) : (
        <TableContainer sx={{ overflowX: "scroll", marginBottom: "2rem" }}>
          <Table
            sx={{
              borderCollapse: "separate",
              borderSpacing: "0 2rem",
            }}
          >
            <TableHead sx={{ backgroundColor: theme.palette.background.paper }}>
              {headers.map((item) => (
                <TableCell
                  align={item.align}
                  key={item.label}
                  sx={
                    item.label === "Nome do motorista"
                      ? {
                          ...styles.table_first_column,
                          ...styles.table_header,
                        }
                      : styles.table_header
                  }
                >
                  {loading ? (
                    <Skeleton animation="wave" height="5rem" />
                  ) : (
                    item.label
                  )}
                </TableCell>
              ))}
            </TableHead>

            <TableBody sx={{ backgroundColor: theme.palette.background.paper }}>
              {data.rows.map((item) => (
                <DriverRow
                  key={String(item.id)}
                  data={item}
                  loading={loading}
                  onEdit={handleLinkData}
                  styles={styles}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!loading && (
        <Stack
          sx={{
            width: "100%",
            alignItems: isMobile ? "center" : "flex-start",
          }}
        >
          <PaginationCustom
            onPageChange={handleOnChangePage}
            onChangeHandle={handleOnChange}
            page={page}
            rowsPerPage={ROWS_PER_PAGE}
            count={data.count ?? 0}
          />
        </Stack>
      )}
    </Fragment>
  );
};

const useStyles = (theme: Theme): Record<string, CSSProperties> => ({
  table_first_column: {
    position: "sticky",
    left: 0,
    backgroundColor: theme.palette.background.paper,
  },
  table_header: {
    fontSize: "1.5rem",
    fontWeight: 600,
    border: "none",
  },
  table_cells: {
    fontSize: "1.5rem",
    border: "none",
  },
  linked_companies: {
    flexDirection: "row",
    justifyContent: "center",
    flexWrap: "wrap",
    gap: "1rem",
    alignItems: "center",
  },
  tag_company: {
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: "8px",
    paddingLeft: ".4rem",
    paddingRight: ".4rem",
    textAlign: "center",
  },
  more_companies: {
    backgroundColor: "#FFF5F7",
    color: theme.palette.error.main,
    fontWeight: 500,
    fontSize: "1.2rem",
    borderRadius: "50%",
    textAlign: "center",
    padding: ".2rem",
  },
});

export { MyDriversTable };
